import { normalizeWireArtifacts } from "@redotech/json/normalize";
import {
  type Message,
  MessageSchema,
} from "@redotech/redo-agents-common/llm-messages";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";
import { ThreadEscalationInfoSchema } from "../merchant-support-agent/message-merchant-support-agent";

/**
 * Thread history shape returned to the Ask Redo UI. This is a
 * minimal projection of `LlmThread` — just what the history dropdown
 * needs to render a row (id, updatedAt, preview text). The full
 * `messages` array is intentionally NOT included here:
 *
 *   1. Strict `MessageSchema` validation rejects legacy/in-progress
 *      message shapes stored on older threads, killing the whole
 *      response payload.
 *   2. The dropdown only renders a one-line preview + date — it
 *      never iterates messages. Sending them was pure waste.
 *
 * To hydrate a full conversation, use `getAskRedoThread` instead
 * (single-thread fetch with permissive parsing on the client side).
 */
/**
 * Fail-soft escalation: a skewed escalation drops the badge but keeps
 * the surrounding row/thread, instead of bricking the whole payload.
 */
const LenientEscalationSchema = zExt
  .filterToValid(ThreadEscalationInfoSchema)
  .optional();

export const AskRedoThreadHistorySchema = z.object({
  _id: zExt.objectId(),
  updatedAt: z.date(),
  // A short preview derived from the most recent user message (used
  // in the thread list sidebar).
  preview: z.string().optional(),
  // Active/most-recent escalation, powering the list status badge.
  escalation: LenientEscalationSchema,
});

export type AskRedoThreadHistory = z.infer<typeof AskRedoThreadHistorySchema>;

/**
 * Fail-soft list: validate each row individually and drop
 * catastrophically-broken ones, so one skewed row can't brick the sidebar.
 */
const LenientThreadHistoryListSchema = zExt.filterToValidArray(
  AskRedoThreadHistorySchema,
);

export const getAskRedoThreads = {
  name: "getAskRedoThreads" as const,
  input: z.object({
    userEmail: z.string().email().describe("User email to filter threads by"),
  }),
  output: z.object({ threads: LenientThreadHistoryListSchema }),
};

/**
 * Fail-soft reopen parsing: normalize + validate each message individually and
 * drop non-conforming ones, so one bad part can't brick the thread.
 */
export const LenientThreadMessagesSchema = z
  .array(z.unknown())
  .transform((messages): Message[] =>
    messages.flatMap((message) => {
      const normalized = MessageSchema.safeParse(
        normalizeWireArtifacts(message),
      );
      return normalized.success ? [normalized.data] : [];
    }),
  );

/**
 * A tool call parked awaiting the merchant's approve/deny decision. Carries
 * only what the persisted tool-call part lacks; the rest is read from there.
 */
const PendingApprovalSchema_ = z.object({
  toolCallId: z.string(),
  prompt: z.string().optional(),
  confirmationData: z.unknown().optional(),
});
export const AskRedoPendingApprovalSchema: z.ZodType<AskRedoPendingApproval> =
  PendingApprovalSchema_;
export interface AskRedoPendingApproval extends z.infer<
  typeof PendingApprovalSchema_
> {}

export const getAskRedoThread = {
  name: "getAskRedoThread" as const,
  input: z.object({ threadId: zExt.objectId() }),
  output: z.object({
    thread: z
      .object({
        _id: zExt.objectId(),
        updatedAt: z.date(),
        messages: z.array(z.unknown()),
        escalation: LenientEscalationSchema,
        // This transform runs server-side (output validation) and again
        // client-side; the flag must ride the wire so the first pass sticks.
        messagesDropped: z.boolean().default(false),
        /** `.default([])` so a server that omits this can't break the read. */
        pendingApprovals: z.array(AskRedoPendingApprovalSchema).default([]),
        /**
         * Set while a turn runs: `messages` stop before the turn's reply and
         * `pendingApprovals` is empty; `watchAskRedoTurn` replays the rest.
         */
        activeTurn: z.object({ turnId: z.string() }).optional(),
      })
      .transform((thread) => {
        const messages = LenientThreadMessagesSchema.parse(thread.messages);
        const messagesDropped =
          thread.messagesDropped || messages.length < thread.messages.length;
        return { ...thread, messages, messagesDropped };
      }),
  }),
};
